import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Heart, PawPrint, ForkKnife, FirstAidKit, House, Users } from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "@/lib/api";
import { fbTrack } from "@/lib/fbpixel";

const PRESETS = [25, 50, 100, 250];

// Impact model — believable, mission-aligned numbers derived from the monthly amount.
function impact(a) {
  return [
    { icon: ForkKnife, label: "Warm meals a month", value: Math.round(a * 2) },
    { icon: FirstAidKit, label: "Days of vet care", value: Math.max(1, Math.round(a / 12)) },
    { icon: House, label: "Nights of safe shelter", value: Math.round(a / 4) },
  ];
}

export default function Sponsor() {
  const [amount, setAmount] = useState(50);
  const [form, setForm] = useState({ name: "", email: "" });
  const [busy, setBusy] = useState(false);
  const [stats, setStats] = useState({ sponsors: 0, monthly_total: 0 });

  useEffect(() => { api.get("/sponsor/stats").then(({ data }) => setStats(data)).catch(() => {}); }, []);

  const pledge = async () => {
    if (!form.email) { toast.error("Add your email so we can confirm your sponsorship"); return; }
    setBusy(true);
    try {
      await api.post("/sponsor/pledge", { name: form.name, email: form.email, amount, frequency: "monthly" });
      fbTrack("Lead", { value: amount, currency: "USD", content_name: "sponsor-pledge" });
      toast.success(`You're in — thank you for sponsoring $${amount}/mo 🐾`);
      api.get("/sponsor/stats").then(({ data }) => setStats(data)).catch(() => {});
    } catch { toast.error("Couldn't record your pledge — try again"); }
    finally { setBusy(false); }
  };

  const sponsorNow = async () => {
    if (!form.email) { toast.error("Add your email first"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/sponsor/subscribe", { amount, name: form.name, email: form.email, origin_url: window.location.origin });
      fbTrack("Subscribe", { value: amount, currency: "USD", predicted_ltv: amount * 12, content_name: "sponsor-monthly" });
      window.location.href = data.checkout_url;
    } catch { toast.error("Couldn't start checkout — try again"); setBusy(false); }
  };

  return (
    <div className="bg-[#050505] min-h-screen" data-testid="sponsor-page">
      {/* Hero */}
      <section className="relative overflow-hidden border-b border-white/10">
        <div className="absolute -left-24 top-0 h-[26rem] w-[26rem] rounded-full soul-pulse pointer-events-none"
          style={{ background: "radial-gradient(circle, rgba(0,229,229,0.35) 0%, rgba(0,229,229,0) 70%)", filter: "blur(14px)" }} />
        <div className="relative max-w-5xl mx-auto px-5 sm:px-8 py-20 text-center">
          <p className="overline text-primary mb-4 inline-flex items-center gap-2"><Heart weight="fill" size={14} /> Monthly sponsorship</p>
          <h1 className="font-display font-black uppercase tracking-tighter leading-[0.9] text-5xl sm:text-7xl">
            Sponsor a <span className="neon-text">pit</span>. Every month.
          </h1>
          <p className="text-muted-foreground mt-5 max-w-2xl mx-auto">A steady heartbeat keeps the ranch alive. Pick your monthly gift, see exactly what it does, and become part of the pack that never quits.</p>
          <div className="mt-8 flex items-center justify-center gap-8">
            <div className="text-center"><div className="font-display font-black text-3xl sm:text-4xl neon-text tabular-nums">{stats.sponsors.toLocaleString()}</div><div className="overline text-muted-foreground text-[10px] mt-1 inline-flex items-center gap-1"><Users weight="fill" size={11} /> active sponsors</div></div>
            <div className="text-center"><div className="font-display font-black text-3xl sm:text-4xl tabular-nums" style={{ color: "#ccff00" }}>${stats.monthly_total.toLocaleString()}</div><div className="overline text-muted-foreground text-[10px] mt-1">raised every month</div></div>
          </div>
        </div>
      </section>

      {/* Calculator */}
      <section className="max-w-3xl mx-auto px-5 sm:px-8 py-16">
        <div className="rounded-2xl border border-white/10 bg-[#0a0a0a] p-6 sm:p-10" data-testid="sponsor-calculator">
          <p className="overline text-muted-foreground text-center">Your monthly gift</p>
          <div className="text-center my-4">
            <span className="font-display font-black text-6xl sm:text-7xl neon-text tabular-nums" data-testid="sponsor-amount">${amount}</span>
            <span className="text-muted-foreground text-lg">/mo</span>
          </div>
          <input type="range" min={10} max={500} step={5} value={amount} onChange={(e) => setAmount(Number(e.target.value))}
            className="w-full accent-pink-500" data-testid="sponsor-slider" />
          <div className="flex flex-wrap justify-center gap-2 mt-5">
            {PRESETS.map((p) => (
              <button key={p} onClick={() => setAmount(p)}
                className={`px-5 py-2 rounded-full border text-sm transition-colors ${amount === p ? "border-primary bg-primary/10 text-white" : "border-white/15 text-muted-foreground hover:border-white/30"}`}
                data-testid={`sponsor-preset-${p}`}>${p}</button>
            ))}
          </div>

          <div className="grid grid-cols-3 gap-3 mt-8" data-testid="sponsor-impact">
            {impact(amount).map((it) => (
              <div key={it.label} className="rounded-xl border border-white/10 bg-black/40 p-4 text-center">
                <it.icon weight="fill" size={22} className="text-secondary mx-auto mb-2" />
                <div className="font-display font-black text-2xl tabular-nums">{it.value.toLocaleString()}</div>
                <div className="overline text-muted-foreground text-[10px] mt-1 leading-tight">{it.label}</div>
              </div>
            ))}
          </div>

          <div className="mt-8 space-y-3">
            <input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} placeholder="Your name"
              className="w-full bg-transparent border border-white/15 rounded-[10px] px-4 py-3 outline-none focus:border-primary transition-colors text-sm" data-testid="sponsor-name" />
            <input value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} placeholder="Email" type="email"
              className="w-full bg-transparent border border-white/15 rounded-[10px] px-4 py-3 outline-none focus:border-primary transition-colors text-sm" data-testid="sponsor-email" />
            <button onClick={sponsorNow} disabled={busy}
              className="w-full inline-flex items-center justify-center gap-2 px-6 py-4 rounded-[10px] font-modern text-black font-semibold transition-transform hover:scale-[1.02] disabled:opacity-50"
              style={{ background: "linear-gradient(90deg,#ff1493,#ccff00)" }} data-testid="sponsor-start">
              <Heart weight="fill" size={18} /> {busy ? "Opening checkout…" : `Sponsor $${amount} / month`}
            </button>
            <button onClick={pledge} disabled={busy}
              className="w-full inline-flex items-center justify-center gap-2 px-6 py-3 rounded-[10px] font-modern border border-primary/40 text-primary hover:bg-primary/10 transition-colors disabled:opacity-50"
              data-testid="sponsor-pledge">
              <PawPrint weight="fill" size={16} /> Just pledge for now (we'll email you)
            </button>
            <p className="text-[11px] text-muted-foreground text-center leading-snug">Secure monthly subscription, billed automatically each month. Cancel anytime — no strings, just pits.</p>
          </div>
        </div>
      </section>
    </div>
  );
}
